import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../services/api';
import './CustomerDocumentsTab.css';

interface AccountDocument {
  id: number;
  title: string;
  description: string;
  folder: string;
  folder_display: string;
  file_url: string;
  file_name: string;
  file_size: number;
  file_size_formatted: string;
  file_type: string;
  file_extension: string;
  download_count: number;
  created_at: string;
}

interface FolderOption {
  key: string;
  name: string;
  count: number;
}

interface CustomerDocumentsTabProps {
  customerId: number;
  customerName: string;
}

const FOLDER_OPTIONS = [
  { key: 'qr_codes', name: 'QR Codes' },
  { key: 'contracts', name: 'Contracts' },
  { key: 'tax_forms', name: 'Tax Forms' },
  { key: 'marketing', name: 'Marketing Materials' },
  { key: 'custom', name: 'Custom/Other' },
];

const CustomerDocumentsTab: React.FC<CustomerDocumentsTabProps> = ({
  customerId,
  customerName,
}) => {
  const [documents, setDocuments] = useState<AccountDocument[]>([]);
  const [folders, setFolders] = useState<FolderOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<string>('');

  // Upload state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadFolder, setUploadFolder] = useState('custom');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit state
  const [editingDoc, setEditingDoc] = useState<AccountDocument | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editFolder, setEditFolder] = useState('');

  useEffect(() => {
    loadDocuments();
  }, [customerId, selectedFolder]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError('');

      let url = `${API_BASE_URL}/admin/customers/${customerId}/documents/`;
      if (selectedFolder) {
        url += `?folder=${selectedFolder}`;
      }

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load documents');
      }

      const data = await response.json();
      setDocuments(data.documents || []);
      setFolders(data.folders || []);
    } catch (err: any) {
      console.error('Error loading documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!uploadFile) {
      alert('Please select a file to upload');
      return;
    }

    if (!uploadTitle.trim()) {
      alert('Please enter a title for the document');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle.trim());
      formData.append('description', uploadDescription.trim());
      formData.append('folder', uploadFolder);

      const response = await fetch(
        `${API_BASE_URL}/admin/customers/${customerId}/documents/`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload document');
      }

      // Reset form
      setShowUploadForm(false);
      setUploadTitle('');
      setUploadDescription('');
      setUploadFolder('custom');
      setUploadFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Reload documents
      loadDocuments();
    } catch (err: any) {
      console.error('Error uploading document:', err);
      alert(err.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingDoc) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/customers/${customerId}/documents/${editingDoc.id}/`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: editTitle.trim(),
            description: editDescription.trim(),
            folder: editFolder,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update document');
      }

      setEditingDoc(null);
      loadDocuments();
    } catch (err: any) {
      console.error('Error updating document:', err);
      alert(err.message || 'Failed to update document');
    }
  };

  const handleDelete = async (doc: AccountDocument) => {
    if (!window.confirm(`Are you sure you want to delete "${doc.title}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/customers/${customerId}/documents/${doc.id}/`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      loadDocuments();
    } catch (err: any) {
      console.error('Error deleting document:', err);
      alert(err.message || 'Failed to delete document');
    }
  };

  const startEdit = (doc: AccountDocument) => {
    setEditingDoc(doc);
    setEditTitle(doc.title);
    setEditDescription(doc.description || '');
    setEditFolder(doc.folder);
  };

  const cancelEdit = () => {
    setEditingDoc(null);
    setEditTitle('');
    setEditDescription('');
    setEditFolder('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getFileIcon = (extension: string) => {
    const ext = extension.toLowerCase();
    if (['pdf'].includes(ext)) return 'PDF';
    if (['doc', 'docx'].includes(ext)) return 'DOC';
    if (['xls', 'xlsx'].includes(ext)) return 'XLS';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'IMG';
    if (['zip', 'rar', '7z'].includes(ext)) return 'ZIP';
    return 'FILE';
  };

  if (loading && documents.length === 0) {
    return (
      <div className="customer-documents-tab">
        <div className="loading-state">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="customer-documents-tab">
      <div className="documents-header">
        <h3>Account Documents</h3>
        <p className="section-description">
          Upload documents specific to {customerName}. These will be visible only to this customer in their portal.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => setShowUploadForm(true)}
        >
          + Upload Document
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Folder Filter */}
      {folders.length > 0 && (
        <div className="folder-filter">
          <button
            className={`folder-chip ${selectedFolder === '' ? 'active' : ''}`}
            onClick={() => setSelectedFolder('')}
          >
            All ({documents.length})
          </button>
          {folders.map((folder) => (
            <button
              key={folder.key}
              className={`folder-chip ${selectedFolder === folder.key ? 'active' : ''}`}
              onClick={() => setSelectedFolder(folder.key)}
            >
              {folder.name} ({folder.count})
            </button>
          ))}
        </div>
      )}

      {/* Upload Form */}
      {showUploadForm && (
        <div className="upload-form-container">
          <form onSubmit={handleUpload} className="upload-form">
            <h4>Upload New Document</h4>

            <div className="form-group">
              <label htmlFor="upload-title">Title *</label>
              <input
                type="text"
                id="upload-title"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Document title"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="upload-description">Description</label>
              <textarea
                id="upload-description"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Optional description"
                rows={2}
              />
            </div>

            <div className="form-group">
              <label htmlFor="upload-folder">Folder</label>
              <select
                id="upload-folder"
                value={uploadFolder}
                onChange={(e) => setUploadFolder(e.target.value)}
              >
                {FOLDER_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="upload-file">File *</label>
              <input
                type="file"
                id="upload-file"
                ref={fileInputRef}
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                required
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowUploadForm(false);
                  setUploadFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Form Modal */}
      {editingDoc && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <form onSubmit={handleEdit}>
              <h4>Edit Document</h4>

              <div className="form-group">
                <label htmlFor="edit-title">Title *</label>
                <input
                  type="text"
                  id="edit-title"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-description">Description</label>
                <textarea
                  id="edit-description"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-folder">Folder</label>
                <select
                  id="edit-folder"
                  value={editFolder}
                  onChange={(e) => setEditFolder(e.target.value)}
                >
                  {FOLDER_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={cancelEdit}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="no-documents">
          <p>No documents uploaded yet.</p>
          <p className="text-muted">
            Upload QR codes, contracts, tax forms, or other customer-specific documents.
          </p>
        </div>
      ) : (
        <div className="documents-list">
          {documents.map((doc) => (
            <div key={doc.id} className="document-card">
              <div className="document-icon">
                <span className="file-type-badge">{getFileIcon(doc.file_extension)}</span>
              </div>
              <div className="document-info">
                <div className="document-title">{doc.title}</div>
                {doc.description && (
                  <div className="document-description">{doc.description}</div>
                )}
                <div className="document-meta">
                  <span className="meta-item">{doc.folder_display}</span>
                  <span className="meta-divider">|</span>
                  <span className="meta-item">{doc.file_size_formatted}</span>
                  <span className="meta-divider">|</span>
                  <span className="meta-item">{formatDate(doc.created_at)}</span>
                  {doc.download_count > 0 && (
                    <>
                      <span className="meta-divider">|</span>
                      <span className="meta-item">{doc.download_count} downloads</span>
                    </>
                  )}
                </div>
              </div>
              <div className="document-actions">
                <a
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-primary"
                >
                  Download
                </a>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => startEdit(doc)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(doc)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerDocumentsTab;
